/**
 * AFAQ-OS API Client â€” unified
 * All API calls go through this module.
 */

const API_BASE = "/api";

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  health: () => apiFetch("/health"),
  modules: () => apiFetch("/modules"),
  features: () => apiFetch("/features"),

  crm: {
    list: (params?: Record<string, any>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return apiFetch(`/crm/leads${qs}`);
    },
    create: (data: any) => apiFetch("/crm/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),
    get: (id: string) => apiFetch(`/crm/leads/${id}`),
    update: (id: string, data: any) => apiFetch(`/crm/leads/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),
    delete: (id: string) => apiFetch(`/crm/leads/${id}`, { method: "DELETE" }),
    pipeline: () => apiFetch("/crm/pipeline/summary"),
  },

  inventory: {
    products: () => apiFetch("/inventory/products"),
    lowStock: () => apiFetch("/inventory/low-stock"),
    summary: () => apiFetch("/inventory/summary"),
  },

  employee: {
    shifts: () => apiFetch("/employee/shifts"),
    kpi: (days?: number) => apiFetch(`/employee/kpi${days ? "?days=" + days : ""}`),
    kpiPerEmployee: (days?: number) => apiFetch(`/employee/kpi/per-employee${days ? "?days=" + days : ""}`),
    logs: (days?: number, name?: string) => apiFetch(`/employee/logs?days=${days ?? 7}${name ? "&employee_name=" + encodeURIComponent(name) : ""}`),
    roster: () => apiFetch("/employee/roster"),
    ping: () => apiFetch("/employee/ping"),
    myDashboard: (name: string) => apiFetch("/employee/my-dashboard?employee_name=" + encodeURIComponent(name)),
    checkIn: (data: any) => apiFetch("/employee/check-in", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    addEmployee: (name: string, role: string = "employee", email?: string) => apiFetch("/employee/employees", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, role, email }) }),
    updateEmployee: (id: string, data: any) => apiFetch("/employee/employees/" + id, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
  },

  marketplace: {
    sellers: () => apiFetch("/marketplace/sellers"),
    seller: (id: string) => apiFetch(`/marketplace/sellers/${id}`),
    register: (data: any) => apiFetch("/marketplace/sellers/register", { method: "POST", body: JSON.stringify(data) }),
    orders: (sellerId?: string) => apiFetch(`/marketplace/orders${sellerId ? "?seller_id=" + sellerId : ""}`),
    createOrder: (data: any) => apiFetch("/marketplace/orders", { method: "POST", body: JSON.stringify(data) }),
    commissions: (sellerId?: string) => apiFetch(`/marketplace/commissions${sellerId ? "?seller_id=" + sellerId : ""}`),
    onboarding: (sellerId: string) => apiFetch(`/marketplace/onboarding/${sellerId}`),
    completeStep: (sellerId: string, step: number) => apiFetch(`/marketplace/onboarding/${sellerId}/step/${step}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }),
    completeOnboardingStep: (sellerId: string, step: number) => apiFetch(`/marketplace/onboarding/${sellerId}/step/${step}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }),
    tierConfig: () => apiFetch("/marketplace/tiers/config"),
    dashboard: (sellerId?: string) => sellerId ? apiFetch(`/marketplace/sellers/${sellerId}/dashboard`) : apiFetch("/marketplace/sellers"),
  },

  accounting: {
    dashboard: () => apiFetch("/accounting/dashboard"),

    accounts: {
      list: () => apiFetch("/accounting/accounts"),
      create: (data: any) => apiFetch("/accounting/accounts", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      setup: () => apiFetch("/accounting/setup", { method: "POST" }),
    },

    transactions: {
      list: (params?: Record<string, any>) => { const qs = params ? "?" + new URLSearchParams(params).toString() : ""; return apiFetch(`/accounting/transactions${qs}`); },
      create: (data: any) => apiFetch("/accounting/transactions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      post: (id: string) => apiFetch(`/accounting/transactions/${id}/post`, { method: "PATCH" }),
      void: (id: string) => apiFetch(`/accounting/transactions/${id}/void`, { method: "PATCH" }),
    },

    invoices: {
      list: (params?: Record<string, any>) => { const qs = params ? "?" + new URLSearchParams(params).toString() : ""; return apiFetch(`/accounting/invoices${qs}`); },
      create: (data: any) => apiFetch("/accounting/invoices", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      updateStatus: (id: string, status: string) => apiFetch(`/accounting/invoices/${id}/status`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) }),
      send: (id: string) => apiFetch(`/accounting/invoices/${id}/send`, { method: "POST" }),
      pay: (id: string, amount: number) => apiFetch(`/accounting/invoices/${id}/pay`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amount }) }),
      void: (id: string) => apiFetch(`/accounting/invoices/${id}/void`, { method: "POST" }),
    },

    expenses: {
      list: (params?: Record<string, any>) => { const qs = params ? "?" + new URLSearchParams(params).toString() : ""; return apiFetch(`/accounting/expenses${qs}`); },
      create: (data: any) => apiFetch("/accounting/expenses", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      approve: (id: string) => apiFetch(`/accounting/expenses/${id}/approve`, { method: "PATCH" }),
    },

    bank: {
      list: (params?: Record<string, any>) => { const qs = params ? "?" + new URLSearchParams(params).toString() : ""; return apiFetch(`/accounting/bank/transactions${qs}`); },
      import: (data: any) => apiFetch("/accounting/bank/import", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      match: (data: any) => apiFetch("/accounting/bank/reconcile", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      exclude: (id: string) => apiFetch(`/accounting/bank/transactions/${id}/exclude`, { method: "PATCH" }),
    },

    reports: {
      balanceSheet: () => apiFetch("/accounting/reports/balance-sheet"),
      incomeStatement: (from: string, to: string) => apiFetch(`/accounting/reports/pnl?from=${from}&to=${to}`),
      trialBalance: () => apiFetch("/accounting/reports/trial-balance"),
      arAging: () => apiFetch("/accounting/reports/ar-aging"),
      cashFlow: (from: string, to: string) => apiFetch(`/accounting/reports/cash-flow?from=${from}&to=${to}`),
    },

    vat: {
      summary: () => apiFetch("/accounting/vat/summary"),
      calculate: (from: string, to: string) => apiFetch("/accounting/vat/returns/calculate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ period_from: from, period_to: to }) }),
      submit: (from: string, to: string) => apiFetch("/accounting/vat/submit", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ period_from: from, period_to: to }) }),
    },

    fta: {
      validateTrn: (trn: string) => apiFetch(`/accounting/fta/validate-trn?trn=${encodeURIComponent(trn)}`),
      checkCompliance: (invoiceId: string) => apiFetch(`/accounting/fta/invoice-compliance/${invoiceId}`),
    },

    payroll: {
      calculate: (data: any) => apiFetch("/accounting/payroll/calculate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
      exportWps: (month?: string) => apiFetch(`/accounting/payroll/wps${month ? "?month=" + month : ""}`),
    },

    shopify: {
      sync: (data: any) => apiFetch("/accounting/sync/shopify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    },

    fullSync: (from: string, to: string) =>
      apiFetch("/accounting/shopify/full-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from_date: from, to_date: to }),
      }),
    syncStatus: () => apiFetch("/accounting/shopify/sync-status"),
    setProcurementCost: (data: { product_title: string; cost_aed: number }) =>
      apiFetch("/accounting/procurement/set-cost", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    procurementCosts: () => apiFetch("/accounting/procurement/costs"),

    syncWithExpenses: (from: string, to: string) =>
      apiFetch("/accounting/shopify/sync-with-expenses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from_date: from, to_date: to }),
      }),
    seedSubscriptions: () =>
      apiFetch("/accounting/expenses/seed-subscriptions", { method: "POST" }),
    monthlySummary: (month: string) =>
      apiFetch(`/accounting/reports/monthly-summary?month=${month}`),

    importCsv: (form: FormData) => apiFetch("/accounting/import/csv", { method: "POST", body: form }),
    importPdf: (form: FormData) => apiFetch("/accounting/import/pdf", { method: "POST", body: form }),
    importImage: (form: FormData) => apiFetch("/accounting/import/image", { method: "POST", body: form }),
    importHistory: () => apiFetch("/accounting/import/history"),
  },

  auth: {
    login: (employeeName: string) => apiFetch("/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ employee_name: employeeName }),
    }),
    logout: () => apiFetch("/auth/logout", { method: "POST" }),
    me: () => apiFetch("/auth/me"),
  },

  notifications: {
    history: (limit?: number) =>
      apiFetch(`/notifications/history${limit ? "?limit=" + limit : ""}`),
    emit: (data: {
      event_type: string;
      title: string;
      message: string;
      notification_type?: string;
      source_module?: string;
      payload?: Record<string, any>;
    }) =>
      apiFetch("/notifications/emit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
  },

  ai: {
    improveText: (text: string, context: string, tone: string = "professional") =>
      apiFetch("/ai/improve-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, context, tone }),
      }),
  },

  aiCenter: {
    notify: (data: any) => apiFetch("/ai/notify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    notifications: (limit?: number) => apiFetch(`/ai/notifications${limit ? "?limit=" + limit : ""}`),
    runWorkflow: (type: string, context: any) => apiFetch("/ai/workflow/run", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type, context }) }),
    workflowHistory: () => apiFetch("/ai/workflow/history"),
    workflowPending: () => apiFetch("/ai/workflow/pending"),
    approveWorkflow: (id: string) => apiFetch(`/ai/workflow/${id}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ approved_by: "admin" }) }),
    rejectWorkflow: (id: string, reason: string) => apiFetch(`/ai/workflow/${id}/reject`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) }),
    disciplineConsult: (data: any) => apiFetch("/ai/discipline/consult", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    warningLetter: (data: any) => apiFetch("/ai/discipline/warning-letter", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    pip: (data: any) => apiFetch("/ai/discipline/pip", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    tutorial: (module: string, role: string, language: string = "english") => apiFetch(`/ai/education/tutorial?module=${module}&role=${role}&language=${language}`),
    productGuide: (brand: string, audience: string) => apiFetch(`/ai/education/product-guide?brand=${brand}&audience=${audience}`),
    coachingTip: (data: any) => apiFetch("/ai/education/coaching-tip", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    settings: () => apiFetch("/ai/settings"),
    testProvider: (provider: string) => apiFetch(`/ai/test?provider=${provider}`),
  },

  hr: {
    directory: () => apiFetch("/employee/hr/directory"),
    createEmployee: (data: any) => apiFetch("/employee/hr/employees", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    updateEmployee: (id: string, data: any) => apiFetch(`/employee/hr/employees/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    kpiReport: (days?: number) => apiFetch(`/employee/hr/kpi-report${days ? "?days=" + days : ""}`),
    leave: (id: string) => apiFetch(`/employee/hr/leave/${id}`),
    requestLeave: (id: string, data: any) => apiFetch(`/employee/hr/leave/${id}/request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    approveLeave: (id: string, data: any) => apiFetch(`/employee/hr/leave/${id}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    payroll: (id: string) => apiFetch(`/employee/hr/payroll/${id}`),
    aiSummary: () => apiFetch("/employee/hr/ai-summary"),
    aiChat: (message: string, history: any[]) => apiFetch("/employee/hr/ai-chat", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message, history }) }),
  },

  phoneBridge: {
    status: () => apiFetch("/phone-bridge/status"),
    devices: () => apiFetch("/phone-bridge/devices"),
    connectMac: () => apiFetch("/phone-bridge/connect/mac", { method: "POST" }),
    connectIp: (host: string, port = 5555) =>
      apiFetch("/phone-bridge/connect/ip", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ host, port }) }),
    disconnect: () => apiFetch("/phone-bridge/disconnect", { method: "POST" }),
    screenshot: () => fetch("/api/phone-bridge/screenshot", { credentials: "include" }),
    tap: (x: number, y: number) =>
      apiFetch("/phone-bridge/input/tap", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ x, y }) }),
    swipe: (x1: number, y1: number, x2: number, y2: number, duration_ms = 300) =>
      apiFetch("/phone-bridge/input/swipe", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ x1, y1, x2, y2, duration_ms }) }),
    inputText: (text: string) =>
      apiFetch("/phone-bridge/input/text", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) }),
    pressKey: (keycode: number) =>
      apiFetch("/phone-bridge/input/key", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ keycode }) }),
    pressBack: () => apiFetch("/phone-bridge/input/back", { method: "POST" }),
    pressHome: () => apiFetch("/phone-bridge/input/home", { method: "POST" }),
    aiAnalyze: () => apiFetch("/phone-bridge/ai/analyze", { method: "POST" }),
  },

  crawler: {
    submitJob: (data: { url: string; max_depth?: number; extract_type: string; tags?: string[] }) =>
      apiFetch("/crawler/jobs", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    jobs: () => apiFetch("/crawler/jobs"),
    job: (id: number) => apiFetch(`/crawler/jobs/${id}`),
    results: (id: number) => apiFetch(`/crawler/jobs/${id}/results`),
    cancelJob: (id: number) => apiFetch(`/crawler/jobs/${id}`, { method: "DELETE" }),
    deleteJob: (id: number) => apiFetch(`/crawler/jobs/${id}`, { method: "DELETE" }),
    status: () => apiFetch("/crawler/status"),
  },

  omnivoice: {
    status: () => apiFetch("/omnivoice/status"),
    assets: (p?: any) => apiFetch("/omnivoice/assets" + (p ? "?" + new URLSearchParams(p) : "")),
    asset: (id: string) => apiFetch(`/omnivoice/assets/${id}`),
    assetsByPhone: (phone: string) => apiFetch(`/omnivoice/assets/phone/${phone}`),
    redeliver: (id: string) => apiFetch(`/omnivoice/assets/${id}/redeliver`, { method: "POST" }),
    groups: () => apiFetch("/omnivoice/groups"),
    registerGroup: (d: any) => apiFetch("/omnivoice/groups/register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    removeGroup: (id: string) => apiFetch(`/omnivoice/groups/${id}`, { method: "DELETE" }),
    connect: (d: any) => apiFetch("/omnivoice/session/connect", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    disconnect: (phone: string) => apiFetch("/omnivoice/session/disconnect", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone }) }),
    profile: (phone: string) => apiFetch(`/omnivoice/profile/${phone}`),
    updateProfile: (phone: string, d: any) => apiFetch(`/omnivoice/profile/${phone}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    stats: () => apiFetch("/omnivoice/stats"),
    test: (d: any) => apiFetch("/omnivoice/test", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
  },

  admin: {
    users: () => apiFetch("/admin/users"),
    createUser: (data: any) => apiFetch("/admin/users", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    updateUser: (id: number, data: any) => apiFetch(`/admin/users/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    deactivateUser: (id: number) => apiFetch(`/admin/users/${id}`, { method: "DELETE" }),
    userPermissions: (id: number) => apiFetch(`/admin/users/${id}/permissions`),
    setPermission: (id: number, data: any) => apiFetch(`/admin/users/${id}/permissions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    permissionsMatrix: () => apiFetch("/admin/permissions/matrix"),
    roles: () => apiFetch("/admin/roles"),
  },

  commission: {
    events: (status?: string, employeeName?: string) => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (employeeName) params.set("employee_name", employeeName);
      const qs = params.toString();
      return apiFetch(qs ? `/commission/events?${qs}` : "/commission/events");
    },
    getEvent: (id: string) => apiFetch(`/commission/events/${id}`),
    process: (data: any) => apiFetch("/commission/process", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    approve: (id: string, approvedBy?: string) => apiFetch(`/commission/events/${id}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ approved_by: approvedBy || "Abdolmadjid" }) }),
    reject: (id: string, reason: string, rejectedBy?: string) => apiFetch(`/commission/events/${id}/reject`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason, rejected_by: rejectedBy || "Abdolmadjid" }) }),
    markPaid: (id: string) => apiFetch(`/commission/events/${id}/paid`, { method: "POST" }),
    rates: () => apiFetch("/commission/rates"),
    setRate: (data: any) => apiFetch("/commission/rates", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    ownership: () => apiFetch("/commission/ownership"),
    reassign: (data: any) => apiFetch("/commission/ownership/reassign", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    reassignCustomer: (data: {
      customer_phone: string;
      new_employee: string;
      admin_name: string;
      reason: string;
    }) => apiFetch("/commission/ownership/reassign", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),
    uploadInvoice: (id: string, form: FormData) =>
      apiFetch(`/commission/events/${id}/upload-invoice`, { method: "POST", body: form }),
    invoices: (id: string) => apiFetch(`/commission/events/${id}/invoices`),
  },

  identity: {
    status: () => apiFetch("/whatsapp/identity/status"),
    reset: (phone: string) => apiFetch("/whatsapp/identity/reset", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone }) }),
    forceVerify: (phone: string) => apiFetch("/whatsapp/identity/force-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone }) }),
  },

  content: {
    status: () => apiFetch("/content/status"),
    socialPost: (d: any) => apiFetch("/content/social-post", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    productDesc: (d: any) => apiFetch("/content/product-description", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    whatsappMsg: (d: any) => apiFetch("/content/whatsapp-message", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    blogPost: (d: any) => apiFetch("/content/blog-post", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    history: () => apiFetch("/content/history"),
    usage: () => apiFetch("/content/usage"),
  },

  agents: {
    list: () => apiFetch("/agents"),
    get: (slug: string) => apiFetch(`/agents/${slug}`),
    status: (slug: string) => apiFetch(`/agents/${slug}/status`),
    trigger: (slug: string) => apiFetch(`/agents/${slug}/trigger`, { method: "POST" }),
    run: (slug: string) => apiFetch(`/agents/${slug}/run`, { method: "POST" }),
    log: (slug: string) => apiFetch(`/agents/${slug}/log`),
    categories: () => apiFetch("/agents/categories"),
    moduleStatus: () => apiFetch("/agents/status"),
    ping: () => apiFetch("/agents-hub/ping"),
    triggerAll: () => apiFetch("/agents-hub/trigger-all", { method: "POST" }),
    hubTrigger: (slug: string) => apiFetch(`/agents-hub/trigger/${slug}`, { method: "POST" }),
    health: () => apiFetch("/agents-hub/health"),
    lastRun: () => apiFetch("/agents-hub/last-run"),
    refresh: () => apiFetch("/agents"),
    statusAll: () => apiFetch("/agents"),
    getStatus: (slug: string) => apiFetch(`/agents/${slug}/status`),
    runManual: (slug: string) => apiFetch(`/agents/${slug}/trigger`, { method: "POST" }),
    history: (slug: string) => apiFetch(`/agents/${slug}/log`),
  },
  email: {
    send: (data: any) => apiFetch("/email/send", {method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(data)}),
    sendBulk: (data: any) => apiFetch("/email/send-bulk", {method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(data)}),
    generate: (data: any) => apiFetch("/email/generate", {method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(data)}),
    log: () => apiFetch("/email/log"),
    templates: () => apiFetch("/email/templates"),
    routingLog: () => apiFetch("/email/routing/log"),
    routingStats: () => apiFetch("/email/routing/stats"),
    routingTrigger: () => apiFetch("/email/routing/trigger", { method: "POST" }),
    routingTest: (d: any) => apiFetch("/email/routing/test", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) }),
    routingRules: () => apiFetch("/email/routing/rules"),
  },

  help: {
    modules: () => apiFetch("/help/modules"),
    articles: (module: string, locale: string = "en", kind?: string, limit: number = 50) => {
      const p = new URLSearchParams({ module, locale, limit: String(limit) });
      if (kind) p.set("kind", kind);
      return apiFetch(`/help/articles?${p.toString()}`);
    },
    get: (id: string) => apiFetch(`/help/articles/${id}`),
    search: (q: string, locale?: string, module?: string, limit: number = 20) => {
      const p = new URLSearchParams({ q, limit: String(limit) });
      if (locale) p.set("locale", locale);
      if (module) p.set("module", module);
      return apiFetch(`/help/search?${p.toString()}`);
    },
    regenerate: (slug: string) => apiFetch(`/help/regenerate/${slug}`, { method: "POST" }),
    regenerateAll: () => apiFetch("/help/regenerate-all", { method: "POST" }),
  },
};
